import React, { useState } from "react";
import { leadSetupApi } from "../../pages/services/leadService";
import { errorText } from "./leadUtils";

// One message in the lead settings: a template on the UraanWapp account
// (WhatsApp or email), what fills each of its {{1}}, {{2}}… and — WhatsApp
// only — an optional file.
//
// value:     { templateId, params: [{ source, value }], attachment: { url, fileName } }
// templates: the account's templates for the channel, as GET
//            /lead-setup/provider-templates returns them
//            ({ id, name, status, sendable, mediaType, params, bodyPreview, subject })
// courseFiles: the course's { courseBrochure, feeStructureBrochure } from the
//            course page — used for the link parameters and as attachments

// How many parameters a template takes: what UraanWapp lists, or the highest
// {{n}} in its text.
export const paramCount = (t) => {
  if (!t) return 0;
  const nums = [...`${t.subject || ""} ${t.bodyPreview || ""}`.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1]));
  return Math.max(nums.length ? Math.max(...nums) : 0, (t.params || []).length);
};

const SAMPLE = {
  lead_name: "Asha Patil", lead_first_name: "Asha", lead_mobile: "9876543210", lead_email: "asha@example.com", lead_city: "Pune",
  course_name: "(course)", course_fee: "₹20,000", course_duration: "3 months", campaign_name: "(campaign)",
  course_brochure_link: "https://api.myskillverse.ai/course/(course)-brochure.pdf", fee_structure_link: "https://api.myskillverse.ai/course/(course)-fee-structure.pdf",
  demo_day: "Saturday", demo_date: "25 Oct 2026", demo_day_date: "Sat, 25 Oct 2026", demo_time: "11:00 AM", demo_link: "https://meet.example/abc",
  payment_link: "https://rzp.io/l/abc", payment_amount: "₹5,000",
};

// The course's own files: what a link parameter or an attachment can point at.
const COURSE_FILES = {
  course_brochure: { field: "courseBrochure", kind: "brochure", label: "Course brochure", link: "course_brochure_link" },
  fee_structure: { field: "feeStructureBrochure", kind: "feeStructure", label: "Fee structure", link: "fee_structure_link" },
};

const TemplateMessageField = ({ channel = "whatsapp", value, onChange, templates, sources, label, courseTitle, courseFiles }) => {
  const isEmail = channel === "email";
  const v = value || {};
  const template = (templates || []).find((t) => t.id === v.templateId);
  const n = paramCount(template);
  const params = Array.from({ length: n }, (_, i) => (v.params || [])[i] || { source: i === 0 ? "lead_first_name" : "custom", value: "" });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const set = (patch) => onChange({ ...v, params, ...patch });
  const setParam = (i, patch) => set({ params: params.map((p, j) => (j === i ? { ...p, ...patch } : p)) });

  const fileFor = (kind) => courseFiles?.[COURSE_FILES[kind].field];
  const linkKind = (source) => Object.keys(COURSE_FILES).find((k) => COURSE_FILES[k].link === source);
  const sample = (p) => {
    if (p.source === "custom") return p.value;
    if (p.source === "course_name" && courseTitle) return courseTitle;
    const kind = linkKind(p.source);
    // The friendly link the lead receives (api.myskillverse.ai/course/<slug>-brochure.pdf).
    if (kind && fileFor(kind)?.url) return courseFiles.fileLinks?.[COURSE_FILES[kind].kind] || fileFor(kind).url;
    return SAMPLE[p.source] || "";
  };
  // Link parameters whose file the course does not have yet: such a message is skipped when due.
  const missingLinks = params.map((p) => linkKind(p.source)).filter((k) => k && courseFiles && !fileFor(k)?.url);
  const attachedCourseFile = !isEmail && v.attachment?.source && COURSE_FILES[v.attachment.source];
  const fill = (text) => String(text || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (_, k) => sample(params[Number(k) - 1] || {}) || `{{${k}}}`);

  // Approved templates first; the rest are listed with their status so a
  // pending one can be prepared, but UraanWapp only sends approved ones.
  const options = [...(templates || [])].sort((a, z) => (z.sendable - a.sendable) || String(a.name).localeCompare(String(z.name)));
  // A saved choice that is no longer on the account still shows, so it can be seen and changed.
  const missing = v.templateId && !template;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const r = await leadSetupApi.uploadAttachment(file);
      set({ attachment: { source: "", url: r.url, fileName: r.fileName } });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ld-wa">
      <div className="ld-wa-title">{label || (isEmail ? "Email" : "WhatsApp message")}</div>
      <select className="ld-select" value={v.templateId || ""} onChange={(e) => set({ templateId: e.target.value || null, params: [] })}>
        <option value="">{isEmail ? "No email" : "No WhatsApp message"}</option>
        {missing && <option value={v.templateId}>{v.templateId} — not on the UraanWapp account</option>}
        {options.map((t) => (
          <option key={t.id} value={t.id}>{t.name}{t.sendable ? "" : ` — ${t.status || "not approved"}`}</option>
        ))}
      </select>
      {missing && <div className="ld-field-error">"{v.templateId}" is not on the UraanWapp account — choose another template.</div>}
      {template && n > 0 && (
        <div style={{ marginTop: 10 }}>
          {params.map((p, i) => (
            <div key={i} className="ld-param-row">
              <span className="ld-param-n">{`{{${i + 1}}}`}</span>
              <select className="ld-select" value={p.source} onChange={(e) => setParam(i, { source: e.target.value })}>
                {sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              {p.source === "custom"
                ? <input className="ld-input" placeholder="Text to send" value={p.value} onChange={(e) => setParam(i, { value: e.target.value })} />
                : <span className="ld-sub">{template.params?.[i]?.label || ""}</span>}
            </div>
          ))}
        </div>
      )}
      {[...new Set(missingLinks)].map((k) => (
        <div key={k} className="ld-field-error">This course has no {COURSE_FILES[k].label.toLowerCase()} yet — upload it on the course page, or this message is skipped.</div>
      ))}
      {template && (
        <div className="ld-wa-preview">
          {isEmail && template.subject && <><b>{fill(template.subject)}</b>{"\n\n"}</>}
          {fill(template.bodyPreview)}
        </div>
      )}
      {template && (
        <div className="ld-sub" style={{ marginTop: 6 }}>
          UraanWapp: <b style={{ color: template.sendable ? "#15803d" : "#b45309" }}>{template.status}</b>
          {!template.sendable && <span className="ld-field-error"> — only approved templates are sent</span>}
          {!isEmail && (template.mediaType ? ` · needs a ${template.mediaType}` : " · text only")}
          {!isEmail && template.mediaType && !v.attachment?.url && !attachedCourseFile && <span className="ld-field-error"> — attach the {template.mediaType} below, or sending fails</span>}
          {!isEmail && !template.mediaType && (v.attachment?.url || attachedCourseFile) && <span className="ld-sub"> — this template takes no file; the attachment is not sent</span>}
        </div>
      )}
      {template && !isEmail && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {attachedCourseFile ? (
            <>
              {fileFor(v.attachment.source)?.url
                ? <a className="ld-chip" href={fileFor(v.attachment.source).url} target="_blank" rel="noreferrer">📎 {attachedCourseFile.label} (from the course page)</a>
                : <span className="ld-field-error">📎 {attachedCourseFile.label} — not uploaded on the course page yet; this message is skipped until it is</span>}
              <button type="button" className="ld-btn ld-btn-sm" onClick={() => set({ attachment: { source: "", url: "", fileName: "" } })}>Remove file</button>
            </>
          ) : v.attachment?.url ? (
            <>
              <a className="ld-chip" href={v.attachment.url} target="_blank" rel="noreferrer">📎 {v.attachment.fileName || "Attachment"}</a>
              <button type="button" className="ld-btn ld-btn-sm" onClick={() => set({ attachment: { source: "", url: "", fileName: "" } })}>Remove file</button>
            </>
          ) : (
            <>
              <label className="ld-btn ld-btn-sm" style={{ cursor: "pointer" }}>
                {uploading ? "Uploading…" : "Attach a file"}
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.pptx" hidden onChange={(e) => upload(e.target.files[0])} />
              </label>
              {Object.entries(COURSE_FILES).map(([kind, f]) => (
                <button key={kind} type="button" className="ld-btn ld-btn-sm" onClick={() => set({ attachment: { source: kind, url: "", fileName: "" } })}>Use the {f.label.toLowerCase()}</button>
              ))}
            </>
          )}
          <span className="ld-sub">PDF, image, Word or PowerPoint · for templates that carry a document</span>
          {error && <span className="ld-field-error">{error}</span>}
        </div>
      )}
    </div>
  );
};

export default TemplateMessageField;
